import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Sparkles, RotateCw, LayoutDashboard, History, Download } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * SummaryOverlay — appears in place over the dashboard at the end of a call.
 * Two stages:
 *   - phase === "loading"  → friendly spinner explaining what's happening
 *   - phase === "ready"    → markdown summary + 3 clear next-step actions
 *
 * The user never feels they "lost" the dashboard — it's just gracefully covered.
 */
export default function SummaryOverlay({
  phase, // "loading" | "ready"
  summary,
  session,
  onStartNew,
  onReturnToDashboard,
}) {
  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[60] bg-slate-900/30 backdrop-blur-[3px] flex items-start justify-center overflow-y-auto"
        data-testid="summary-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          className="mt-20 mb-12 mx-4 max-w-3xl w-full bg-white border border-slate-200 rounded-2xl shadow-xl"
        >
          {phase === "loading" ? <LoadingStage /> : <ReadyStage summary={summary} session={session} onStartNew={onStartNew} onReturnToDashboard={onReturnToDashboard} />}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function LoadingStage() {
  return (
    <div className="p-10 sm:p-12 text-center" data-testid="summary-loading-stage">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-white mb-5">
        <Loader2 className="h-7 w-7 animate-spin" />
      </div>
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Wrapping up</div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2">
        Generating your call summary
      </h2>
      <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
        SCH is reviewing the transcript, consolidating insights, and writing a structured recap.
        This usually takes 5–10 seconds. Hang tight.
      </p>
      <div className="mt-8 flex justify-center gap-2">
        <Dot delay={0} />
        <Dot delay={120} />
        <Dot delay={240} />
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <motion.span
      className="h-2 w-2 rounded-full bg-slate-400"
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 1.2, repeat: Infinity, delay: delay / 1000 }}
    />
  );
}

function ReadyStage({ summary, session, onStartNew, onReturnToDashboard }) {
  const downloadMd = () => {
    if (!summary) return;
    const blob = new Blob([summary], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${session?.title || "call-summary"}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="summary-ready-stage">
      <header className="px-8 pt-8 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-700 mb-2">
          <Sparkles className="h-3.5 w-3.5" />
          Call complete
        </div>
        <div className="flex items-start justify-between gap-4">
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Post-Call Summary
          </h2>
          <button
            onClick={downloadMd}
            data-testid="overlay-download-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 hover:border-slate-500 hover:bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors"
          >
            <Download className="h-3.5 w-3.5" /> Markdown
          </button>
        </div>
        {session?.title && (
          <div className="text-sm text-slate-500 mt-1">{session.title}</div>
        )}
      </header>

      <article
        className="px-8 py-6 max-h-[55vh] overflow-y-auto sch-scroll whitespace-pre-wrap text-sm leading-relaxed text-slate-800"
        data-testid="overlay-summary-content"
      >
        {summary || "No summary generated."}
      </article>

      <footer className="px-8 py-5 border-t border-slate-100 bg-slate-50/60 rounded-b-2xl">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            onClick={onStartNew}
            data-testid="action-start-new-call"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 text-sm font-medium transition-colors"
          >
            <RotateCw className="h-4 w-4" /> Start New Call
          </button>
          <button
            onClick={onReturnToDashboard}
            data-testid="action-return-dashboard"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 hover:border-slate-500 hover:bg-white text-slate-800 px-4 py-2.5 text-sm font-medium transition-colors"
          >
            <LayoutDashboard className="h-4 w-4" /> Return to Dashboard
          </button>
          <Link
            to="/sessions"
            data-testid="action-view-previous-calls"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 hover:border-slate-500 hover:bg-white text-slate-800 px-4 py-2.5 text-sm font-medium transition-colors"
          >
            <History className="h-4 w-4" /> View Previous Calls
          </Link>
        </div>
      </footer>
    </div>
  );
}
